import { AxiosResponse } from "axios";
import http from "utilities/http-common";

const baseUrl = "/api/Company";

export interface IUpdateCompanyDto {
    id: string;
    name: string;
    childCompanyIds: string[];
}

export async function getList(
    name?: string,
    tenantId?: string,
): Promise<Company[]> {
    const params = new URLSearchParams();

    if (name) params.append("name", name);
    if (tenantId) params.append("tenantId", tenantId);
    return http
        .get(`${baseUrl}`, { params: params })
        .then((res: AxiosResponse<Company[]>) => res.data);
}

export async function getAll(
    page: number,
    pageSize: number,
    direction: string,
    sort: string,
    name: string,
    validNames: string,
    tenantId?: string,
): Promise<PagedResponse<Company>> {
    const params = new URLSearchParams();

    params.append("page", page.toString());
    if (pageSize) params.append("pageSize", pageSize.toString());
    if (direction) params.append("direction", direction);
    if (sort) params.append("sortBy", sort);
    if (name) params.append("name", name);
    if (validNames) params.append("validNames", validNames);
    if (tenantId) params.append("tenantId", tenantId);

    return http
        .get(`${baseUrl}/paged`, { params: params })
        .then((res: AxiosResponse<PagedResponse<Company>>) => res.data);
}

export async function getResponsibles(
    page: number,
    pageSize: number,
    direction: string,
    sort: string,
    name: string,
    responsible: string,
    responsibleFirstName: string,
    responsibleName: string,
    tenantId?: string,
): Promise<PagedResponse<ResponsiblePaged>> {
    const params = new URLSearchParams();

    if (page) params.append("page", page.toString());
    if (pageSize) params.append("pageSize", pageSize.toString());
    if (direction) params.append("direction", direction);
    if (sort) params.append("sortBy", sort);
    if (name) params.append("name", name);
    if (responsible) params.append("responsible", responsible);
    if (responsibleFirstName)
        params.append("responsibleFirstName", responsibleFirstName);
    if (responsibleName) params.append("responsibleName", responsibleName);
    if (tenantId) params.append("tenantId", tenantId);

    return http
        .get(`${baseUrl}/responsible-paged`, { params: params })
        .then(
            (res: AxiosResponse<PagedResponse<ResponsiblePaged>>) => res.data,
        );
}

export async function createResponsible(
    companyIds: string[],
    email: string,
    name?: string,
    firstName?: string,
    tenantId?: string,
): Promise<Company> {
    const params = new URLSearchParams();
    if (tenantId) params.append("tenantId", tenantId);

    return http
        .post(
            `${baseUrl}/responsible`,
            {
                companyIds: companyIds,
                email: email,
                name: name,
                firstName: firstName,
            },
            { params: params },
        )
        .then((res: AxiosResponse<Company>) => res.data);
}

export async function updateResponsible(
    userId: string,
    companyIds: string[],
    email: string,
    name?: string,
    firstName?: string,
    tenantId?: string,
): Promise<Company> {
    const params = new URLSearchParams();
    if (tenantId) params.append("tenantId", tenantId);

    return http
        .put(
            `${baseUrl}/responsible`,
            {
                userId: userId,
                companyIds: companyIds,
                email: email,
                name: name,
                firstName: firstName,
            },
            { params: params },
        )
        .then((res: AxiosResponse<Company>) => res.data);
}

export async function update(company: IUpdateCompanyDto, tenantId?: string): Promise<Company> {
    const params = new URLSearchParams();
    if (tenantId) params.append("tenantId", tenantId);

    return http
        .put(`${baseUrl}/${company.id}`, company, { params: params })
        .then((res: AxiosResponse<Company>) => res.data);
}

export async function create(
    name: string,
    validNames: string[],
    tenantId?: string
): Promise<Company> {
    const params = new URLSearchParams();
    if (tenantId) params.append("tenantId", tenantId);

    return http
        .post(`${baseUrl}`, { name: name, validNames: validNames }, { params: params })
        .then((res: AxiosResponse<Company>) => res.data);
}

export async function deleteCompany(id: string, tenantId?: string): Promise<void> {
    const params = new URLSearchParams();
    if (tenantId) params.append("tenantId", tenantId);
    return http.delete(`${baseUrl}/${id}`, { params: params });
}

export async function mergeCompanies(
    selectedId: string,
    companyIds: string[],
    tenantId?: string
): Promise<Company> {
    const params = new URLSearchParams();
    params.append("id", selectedId);
    if (tenantId) params.append("tenantId", tenantId);
    return http.put(`${baseUrl}`, companyIds, { params: params });
}
